import { HTMLAttributes, useCallback, useEffect, useState } from 'react'
import { useMedia } from 'react-use'
import useEmblaCarousel from 'embla-carousel-react'

import Container from '@/components/base/Container'
import ImageCard, { ImageCardProps } from '@/components/base/ImageCard'
import Section from '@/components/base/Section'
import Typography, { TypographyProps } from '@/components/base/Typography'

import { cn } from '@/lib/utils'

export type ImageCardsProps = HTMLAttributes<HTMLElement> &
  Partial<{
    title: string
    title_variant: TypographyProps['variant']
    title_component: TypographyProps['component']
    collection: ImageCardProps[]
  }>

const ImageCards = ({
  title,
  title_variant,
  title_component,
  collection = [],
  className,
  ...props
}: ImageCardsProps) => {
  const [ref, emblaApi] = useEmblaCarousel({ align: 'start' })
  const [needsCarousel, setNeedsCarousel] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const isLgScreen = useMedia('(min-width: 1024px)', false)
  const isMdScreen = useMedia(
    '(min-width: 768px) and (max-width: 1023px)',
    false
  )

  const onInit = useCallback(() => {
    if (!emblaApi) return
    setScrollSnaps(emblaApi.scrollSnapList())
  }, [emblaApi])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    setNeedsCarousel(
      (isLgScreen && collection.length > 3) ||
        (isMdScreen && collection.length > 2) ||
        (!isLgScreen && !isMdScreen && collection.length > 1)
    )
  }, [collection, isMdScreen, isLgScreen])

  useEffect(() => {
    if (!emblaApi) return
    emblaApi.reInit({ active: needsCarousel })
  }, [needsCarousel, emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    onInit()
    onSelect()
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onInit, onSelect])

  return (
    <Section className={cn([className])} {...props}>
      <Container>
        <div className="grid gap-y-gutter">
          <div className="flex justify-end">
            {title ? (
              <Typography
                variant={title_variant}
                component={title_component}
                className="grow text-heading"
              >
                {title}
              </Typography>
            ) : null}

            {scrollSnaps.length > 1 ? (
              <div className="flex items-center space-x-[.3rem]">
                {scrollSnaps.map((_, index) => (
                  <div key={index} className="flex shrink-0">
                    <button className="-m-[.3rem] flex p-[.3rem]">
                      <span className="sr-only">{`Slide ${index}`}</span>
                      <span
                        className={cn([
                          'flex h-[.8rem] w-[.8rem] rounded-full border-[.1rem] border-foreground',
                          {
                            'bg-foreground': index === selectedIndex
                          }
                        ])}
                      />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {collection.length > 0 ? (
            <div ref={ref} className="-mx-margin overflow-hidden px-margin">
              <div className="-ml-gutter flex touch-pan-y">
                {collection.map((card, index) => (
                  <div
                    key={`image-card-${index}`}
                    className={cn([
                      'min-w-0 shrink-0 grow-0 basis-[75%] pl-gutter md:basis-1/2 lg:basis-1/3',
                      'lg:group-[.modal]:basis-1/2'
                    ])}
                  >
                    <ImageCard {...card} />
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </Container>
    </Section>
  )
}

export default ImageCards
